import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trpc } from "../lib/trpc.js";
import { Button } from "../components/ui/Button.js";
import { NavActions } from "../components/NavActions.js";
import { Input } from "../components/ui/Input.js";

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: "var(--color-accent)" }}>
      {children}
    </p>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl p-5 space-y-4" style={{ background: "var(--color-card)", border: "1px solid var(--color-border)", boxShadow: "var(--shadow-sm)" }}>
      {children}
    </div>
  );
}

export function AccountSettings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const { data: me } = trpc.auth.me.useQuery();

  const [displayName, setDisplayName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [setNewPassword2, setSetNewPassword] = useState("");
  const [setConfirmPassword2, setSetConfirmPassword] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const updateName = trpc.account.updateDisplayName.useMutation({
    onSuccess: () => { utils.auth.me.invalidate(); setDisplayName(""); },
  });
  const changePassword = trpc.account.changePassword.useMutation({
    onSuccess: () => { setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); },
  });
  const setPassword = trpc.account.setPassword.useMutation({
    onSuccess: () => {
      utils.auth.me.invalidate();
      setSetNewPassword("");
      setSetConfirmPassword("");
    },
  });
  const deactivate = trpc.account.deactivate.useMutation({
    onSuccess: () => navigate("/login"),
  });
  const deleteAccount = trpc.account.deleteAccount.useMutation({
    onSuccess: () => navigate("/"),
  });

  function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) return;
    changePassword.mutate({ currentPassword, newPassword });
  }

  function handleSetPasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (setNewPassword2 !== setConfirmPassword2) return;
    setPassword.mutate({ newPassword: setNewPassword2 });
  }

  function handleDeleteSubmit(e: React.FormEvent) {
    e.preventDefault();
    deleteAccount.mutate({ password: deletePassword || undefined });
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
      <header
        className="sticky top-0 z-10 flex items-center gap-3 px-6 py-4 md:px-10"
        style={{
          background: "oklch(97% 0.014 82 / 0.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <Link to="/dashboard" className="text-sm flex-shrink-0" style={{ color: "var(--color-muted-foreground)", textDecoration: "none" }}>
          {t("nav.backToDashboard")}
        </Link>
        <span style={{ color: "var(--color-border-strong)" }}>|</span>
        <span className="font-serif font-semibold" style={{ color: "var(--color-foreground)" }}>{t("account.title")}</span>
        <div className="ml-auto"><NavActions /></div>
      </header>

      <main className="mx-auto max-w-lg px-6 py-10 space-y-8 md:px-10">

        {/* Display name */}
        <div style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) both" }}>
          <SectionLabel>{t("account.profile.title")}</SectionLabel>
          <Card>
            <div>
              <p className="text-xs mb-1" style={{ color: "var(--color-muted-foreground)" }}>{t("account.profile.nameLabel")}</p>
              <p className="text-sm font-medium" style={{ color: "var(--color-foreground)" }}>{me?.displayName}</p>
            </div>
            <form
              className="flex gap-2"
              onSubmit={(e) => { e.preventDefault(); if (displayName.trim()) updateName.mutate({ displayName: displayName.trim() }); }}
            >
              <Input
                placeholder={t("account.profile.nameLabel")}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" size="sm" loading={updateName.isPending} disabled={!displayName.trim()}>
                {t("account.profile.updateName")}
              </Button>
            </form>
            {updateName.isSuccess && (
              <p className="text-xs" style={{ color: "var(--color-accent)" }}>{t("account.profile.nameUpdated")}</p>
            )}
            {updateName.error && (
              <p className="text-xs" style={{ color: "var(--color-destructive)" }}>{updateName.error.message}</p>
            )}
          </Card>
        </div>

        {/* Password — change if set, set if OAuth-only */}
        {me && (
          <div style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both" }}>
            <SectionLabel>{t("account.password.title")}</SectionLabel>
            <Card>
              {me.hasPassword ? (
                <form className="space-y-3" onSubmit={handlePasswordSubmit}>
                  <Input
                    label={t("account.password.currentLabel")}
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                  <Input
                    label={t("account.password.newLabel")}
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <Input
                    label={t("account.password.confirmLabel")}
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    hint={newPassword && confirmPassword && newPassword !== confirmPassword ? t("account.password.mismatch") : undefined}
                    required
                  />
                  {changePassword.error && (
                    <p className="text-xs" style={{ color: "var(--color-destructive)" }}>{changePassword.error.message}</p>
                  )}
                  {changePassword.isSuccess && (
                    <p className="text-xs" style={{ color: "var(--color-accent)" }}>{t("account.password.changed")}</p>
                  )}
                  <Button
                    type="submit"
                    size="sm"
                    loading={changePassword.isPending}
                    disabled={!currentPassword || !newPassword || newPassword !== confirmPassword}
                  >
                    {t("account.password.changeButton")}
                  </Button>
                </form>
              ) : (
                <form className="space-y-3" onSubmit={handleSetPasswordSubmit}>
                  <p className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>
                    {t("account.password.setDescription")}
                  </p>
                  <Input
                    label={t("account.password.setNewLabel")}
                    type="password"
                    value={setNewPassword2}
                    onChange={(e) => setSetNewPassword(e.target.value)}
                    hint="Minimum 8 characters"
                    required
                  />
                  <Input
                    label={t("account.password.setConfirmLabel")}
                    type="password"
                    value={setConfirmPassword2}
                    onChange={(e) => setSetConfirmPassword(e.target.value)}
                    hint={setNewPassword2 && setConfirmPassword2 && setNewPassword2 !== setConfirmPassword2 ? t("account.password.mismatch") : undefined}
                    required
                  />
                  {setPassword.error && (
                    <p className="text-xs" style={{ color: "var(--color-destructive)" }}>{setPassword.error.message}</p>
                  )}
                  {setPassword.isSuccess && (
                    <p className="text-xs" style={{ color: "var(--color-accent)" }}>{t("account.password.setSuccess")}</p>
                  )}
                  <Button
                    type="submit"
                    size="sm"
                    loading={setPassword.isPending}
                    disabled={!setNewPassword2 || setNewPassword2 !== setConfirmPassword2}
                  >
                    {t("account.password.setButton")}
                  </Button>
                </form>
              )}
            </Card>
          </div>
        )}

        {/* Danger zone */}
        <div style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both" }}>
          <SectionLabel>{t("account.danger.title")}</SectionLabel>
          <Card>
            <div className="space-y-4">
              {/* Deactivate */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium" style={{ color: "var(--color-foreground)" }}>{t("account.danger.deactivate.title")}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>
                    {t("account.danger.deactivate.description")}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={deactivate.isPending}
                  style={{ color: "var(--color-destructive)", flexShrink: 0 }}
                  onClick={() => {
                    if (confirm(t("account.danger.deactivate.confirm")))
                      deactivate.mutate();
                  }}
                >
                  {t("account.danger.deactivate.button")}
                </Button>
              </div>

              <div style={{ height: "1px", background: "var(--color-border)" }} />

              {/* Delete */}
              <div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium" style={{ color: "var(--color-destructive)" }}>{t("account.danger.delete.title")}</p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>
                      {t("account.danger.delete.description")}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    style={{ color: "var(--color-destructive)", flexShrink: 0 }}
                    onClick={() => setShowDeleteConfirm((v) => !v)}
                  >
                    {t("account.danger.delete.button")}
                  </Button>
                </div>

                {showDeleteConfirm && (
                  <form className="mt-4 space-y-3" onSubmit={handleDeleteSubmit}>
                    <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                      {t("account.danger.deactivate.warning")}
                    </p>
                    {me?.hasPassword && (
                      <Input
                        label={t("account.danger.delete.passwordLabel")}
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        required
                      />
                    )}
                    {deleteAccount.error && (
                      <p className="text-xs" style={{ color: "var(--color-destructive)" }}>{deleteAccount.error.message}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        size="sm"
                        loading={deleteAccount.isPending}
                        style={{ background: "var(--color-destructive)", color: "white" }}
                      >
                        {t("account.danger.delete.confirm")}
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(false)}>
                        {t("common.cancel")}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
